import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Per-item view state (expanded, loading, …) keyed by item id — what EMMET used
 * Akita's `EntityUIStore`/`EntityUIQuery` for. Every consumer wants the same shape,
 * a record keyed by id, so that is the whole surface.
 *
 * Unlike Akita's `setInitialEntityState`, a missing id does **not** resolve to an
 * initial value. `byId$` says so in its type — `TUi | undefined` per key — which makes
 * the strict-template compiler require `?.` and a default at every read, rather than
 * merely tolerating it. Without that, an unguarded `[id].loading` type-checks and
 * throws at runtime (`noUncheckedIndexedAccess` is off, so a bare `Record` would not
 * catch it). See STEP-4D-PLAN.md.
 */
export class ItemUiState<TUi> {
  private readonly state$ = new BehaviorSubject<Record<string, TUi>>({});

  public readonly byId$: Observable<Record<string, TUi | undefined>> = this.state$.asObservable();

  public set(id: string, ui: TUi): void {
    this.state$.next({ ...this.state$.value, [id]: ui });
  }

  /** One emission for the whole batch, as Akita's `@transaction()` gave. */
  public setMany(entries: Array<[id: string, ui: TUi]>): void {
    this.state$.next({ ...this.state$.value, ...Object.fromEntries(entries) });
  }

  /**
   * Merges into the given ids only, creating them if absent — Akita's
   * `ui.update(ids, partial)`. Creation works because spreading a missing key yields
   * `{}` rather than throwing, so `partial` must cover every required field of `TUi`
   * when the id may be new.
   */
  public patch(ids: string[], partial: Partial<TUi>): void {
    const current = this.state$.value;

    this.state$.next({
      ...current,
      ...Object.fromEntries(ids.map((id) => [id, { ...current[id], ...partial } as TUi])),
    });
  }

  /** Akita's `ui.update(partial)` with no id: applies to every known item. */
  public patchAll(partial: Partial<TUi>): void {
    this.patch(Object.keys(this.state$.value), partial);
  }
}
